import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
    Upload, Zap, Loader2, TrendingUp, TrendingDown, Target, Shield, Clock, ChevronRight, ImagePlus, X
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [analysis, setAnalysis] = useState(null);
    const [history, setHistory] = useState([]);
    const [loadingAnalysis, setLoadingAnalysis] = useState(false);
    const [dragOver, setDragOver] = useState(false);

    const fetchHistory = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/analysis/history?limit=20`, { withCredentials: true });
            setHistory(data.analyses || []);
        } catch { /* silent */ }
    }, []);

    useEffect(() => { fetchHistory(); }, [fetchHistory]);

    const handleFile = (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Please upload an image file (PNG, JPG, etc.)");
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            toast.error("Image too large. Max 10MB.");
            return;
        }
        setImageFile(file);
        setAnalysis(null);
        const reader = new FileReader();
        reader.onload = (e) => setImagePreview(e.target.result);
        reader.readAsDataURL(file);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files[0];
        handleFile(file);
    };

    const handleAnalyze = async () => {
        if (!imageFile) return;
        if (user?.subscription_status === "expired") {
            toast.error("Subscription expired. Please subscribe.");
            navigate("/pricing");
            return;
        }
        setLoadingAnalysis(true);
        setAnalysis(null);
        try {
            const formData = new FormData();
            formData.append("file", imageFile);
            const { data } = await axios.post(`${API}/analysis/chart-upload`, formData, {
                withCredentials: true,
                headers: { "Content-Type": "multipart/form-data" },
            });
            setAnalysis(data);
            fetchHistory();
            toast.success("Analysis complete!");
        } catch (e) {
            const msg = e.response?.data?.detail || "Failed to analyze chart";
            toast.error(typeof msg === "string" ? msg : "Analysis error");
            if (e.response?.status === 403) navigate("/pricing");
        }
        setLoadingAnalysis(false);
    };

    const clearImage = () => {
        setImageFile(null);
        setImagePreview(null);
        setAnalysis(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const levels = analysis?.analysis?.chart_levels_pct;

    return (
        <div className="h-screen bg-[#0A0A0A] flex flex-col lg:overflow-hidden overflow-auto" data-testid="dashboard-page">
            <Navbar />
            <div className="flex-1 flex flex-col lg:flex-row lg:overflow-hidden lg:min-h-0">

                {/* Main Area - Chart Upload */}
                <div className="flex-1 flex flex-col min-w-0 lg:min-h-0 lg:overflow-hidden">
                    {/* Header */}
                    <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 flex-shrink-0">
                        <div className="flex items-center gap-3">
                            <ImagePlus className="w-5 h-5 text-[#007AFF]" />
                            <span className="text-sm font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                {analysis ? analysis.symbol : "Upload Chart"}
                            </span>
                            {analysis && (
                                <Badge variant="outline" className="rounded-full text-[10px] text-zinc-400 border-white/10">
                                    {analysis.timeframe}
                                </Badge>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            {imagePreview && (
                                <Button variant="ghost" onClick={clearImage} className="text-zinc-500 hover:text-white rounded-xl text-xs h-8 px-3" data-testid="clear-image-btn">
                                    <X className="w-3 h-3 mr-1" /> Clear
                                </Button>
                            )}
                            <Button
                                onClick={handleAnalyze}
                                disabled={!imageFile || loadingAnalysis}
                                className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-xl px-5 font-bold text-sm h-9 shadow-md shadow-[#007AFF]/20"
                                data-testid="analyze-btn"
                            >
                                {loadingAnalysis ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</> : <><Zap className="w-4 h-4 mr-2" /> Analyze Chart</>}
                            </Button>
                        </div>
                    </div>

                    {/* Chart Area */}
                    <div className="flex-1 min-h-[300px] lg:min-h-0 p-3 relative" data-testid="chart-area">
                        {!imagePreview ? (
                            /* Upload Zone */
                            <div
                                className={`h-full flex flex-col items-center justify-center border-2 border-dashed rounded-2xl transition-all cursor-pointer ${dragOver ? 'border-[#007AFF] bg-[#007AFF]/5' : 'border-white/10 hover:border-white/20 hover:bg-white/[0.02]'}`}
                                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                onDragLeave={() => setDragOver(false)}
                                onDrop={handleDrop}
                                onClick={() => fileInputRef.current?.click()}
                                data-testid="upload-zone"
                            >
                                <Upload className={`w-12 h-12 mb-4 ${dragOver ? 'text-[#007AFF]' : 'text-zinc-600'}`} />
                                <p className="text-base font-semibold text-white mb-1">Drop your chart screenshot here</p>
                                <p className="text-sm text-zinc-500 mb-4">or click to browse</p>
                                <p className="text-xs text-zinc-600">PNG, JPG, WEBP up to 10MB</p>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => handleFile(e.target.files[0])}
                                    data-testid="file-input"
                                />
                            </div>
                        ) : (
                            /* Uploaded Chart with Level Overlays */
                            <div className="h-full relative rounded-xl overflow-hidden bg-[#121212]" data-testid="chart-display">
                                <img
                                    src={imagePreview}
                                    alt="Trading chart"
                                    className="w-full h-full object-contain"
                                    data-testid="chart-image"
                                />
                                {/* Level overlay lines */}
                                {analysis && levels && (
                                    <div className="absolute inset-0 pointer-events-none">
                                        {levels.entry != null && (
                                            <LevelLine pct={levels.entry} color="#007AFF" label={`ENTRY $${analysis.analysis.entry_price}`} />
                                        )}
                                        {levels.stop_loss != null && (
                                            <LevelLine pct={levels.stop_loss} color="#FF3B30" label={`SL $${analysis.analysis.stop_loss}`} />
                                        )}
                                        {levels.tp1 != null && (
                                            <LevelLine pct={levels.tp1} color="#00C805" label={`TP1 $${analysis.analysis.take_profit_1}`} />
                                        )}
                                        {levels.tp2 != null && (
                                            <LevelLine pct={levels.tp2} color="#00C805" label={`TP2 $${analysis.analysis.take_profit_2}`} dashed />
                                        )}
                                        {levels.tp3 != null && (
                                            <LevelLine pct={levels.tp3} color="#00C805" label={`TP3 $${analysis.analysis.take_profit_3}`} dashed />
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Panel - Analysis Results */}
                <aside className="w-full lg:w-96 border-t lg:border-t-0 lg:border-l border-white/10 flex flex-col lg:overflow-hidden flex-shrink-0" data-testid="analysis-panel">
                    <div className="px-5 py-3 border-b border-white/10 flex-shrink-0">
                        <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500">Analysis</p>
                    </div>
                    <ScrollArea className="flex-1">
                        {loadingAnalysis ? (
                            <div className="p-5 flex flex-col items-center justify-center py-16">
                                <Loader2 className="w-8 h-8 text-[#007AFF] animate-spin mb-4" />
                                <p className="text-sm text-zinc-400">Analyzing your chart...</p>
                                <p className="text-xs text-zinc-600 mt-1">This may take a few seconds</p>
                            </div>
                        ) : analysis ? (
                            <AnalysisResult data={analysis} />
                        ) : (
                            <div className="p-6 text-center py-16">
                                <Target className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
                                <p className="text-base text-zinc-400 mb-2">Upload a chart screenshot</p>
                                <p className="text-sm text-zinc-600">Take a screenshot from TradingView or any charting platform, upload it here, and get AI-powered analysis with precise entry, stop loss, and take profit levels.</p>
                            </div>
                        )}
                    </ScrollArea>

                    {/* History */}
                    {history.length > 0 && (
                        <>
                            <Separator className="bg-white/10" />
                            <div className="px-5 py-3 border-b border-white/10 flex-shrink-0">
                                <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500">Recent Analyses</p>
                            </div>
                            <ScrollArea className="max-h-52">
                                {history.slice(0, 10).map((h) => (
                                    <button
                                        key={h.id}
                                        onClick={() => setAnalysis(h)}
                                        className="w-full flex items-center justify-between px-5 py-2.5 hover:bg-white/[0.03] transition-all text-left"
                                        data-testid={`history-${h.id}`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Badge variant="outline" className={`rounded-full text-[9px] border px-1.5 py-0 ${h.analysis?.direction === 'LONG' ? 'text-[#00C805] border-[#00C805]/30' : 'text-[#FF3B30] border-[#FF3B30]/30'}`}>
                                                {h.analysis?.direction}
                                            </Badge>
                                            <span className="text-sm font-semibold text-white">{h.symbol}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-[10px] text-zinc-500">{h.timeframe}</span>
                                            <ChevronRight className="w-3 h-3 text-zinc-600" />
                                        </div>
                                    </button>
                                ))}
                            </ScrollArea>
                        </>
                    )}
                </aside>
            </div>
        </div>
    );
}

function LevelLine({ pct, color, label, dashed }) {
    return (
        <div className="absolute left-0 right-0" style={{ top: `${Math.min(Math.max(pct, 2), 98)}%` }}>
            <div className="relative flex items-center">
                <div
                    className="flex-1 h-[1.5px]"
                    style={{
                        backgroundColor: color,
                        opacity: 0.8,
                        backgroundImage: dashed ? `repeating-linear-gradient(90deg, ${color} 0px, ${color} 6px, transparent 6px, transparent 12px)` : 'none',
                        backgroundColor: dashed ? 'transparent' : color,
                    }}
                />
                <span
                    className="absolute right-2 text-[10px] font-mono font-bold px-1.5 py-0.5 rounded"
                    style={{ color: '#fff', backgroundColor: color, fontSize: '10px' }}
                >
                    {label}
                </span>
            </div>
        </div>
    );
}

function AnalysisResult({ data }) {
    const a = data.analysis || {};
    const isLong = a.direction === "LONG";

    return (
        <div className="p-5 space-y-5 animate-fadeIn" data-testid="analysis-result">
            {/* Instrument & Direction */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Badge className={`rounded-full text-sm font-bold px-4 py-1.5 ${isLong ? 'bg-[#00C805] text-white' : 'bg-[#FF3B30] text-white'}`} data-testid="analysis-direction">
                        {isLong ? <TrendingUp className="w-4 h-4 mr-1.5" /> : <TrendingDown className="w-4 h-4 mr-1.5" />}
                        {a.direction}
                    </Badge>
                    <span className="text-lg font-bold text-white">{data.symbol}</span>
                </div>
                <Badge variant="outline" className={`rounded-full text-xs border px-3 py-1 ${a.confidence === 'HIGH' ? 'text-[#00C805] border-[#00C805]/30' : a.confidence === 'MEDIUM' ? 'text-[#FF9F0A] border-[#FF9F0A]/30' : 'text-zinc-400 border-zinc-600'}`} data-testid="analysis-confidence">
                    {a.confidence}
                </Badge>
            </div>

            {/* Levels - Bigger text */}
            <div className="space-y-3 bg-white/[0.03] rounded-xl p-4">
                <LevelRow icon={Target} label="ENTRY" value={a.entry_price} color="#007AFF" />
                <Separator className="bg-white/5" />
                <LevelRow icon={Shield} label="STOP LOSS" value={a.stop_loss} color="#FF3B30" />
                <Separator className="bg-white/5" />
                <LevelRow icon={Zap} label="TP 1" value={a.take_profit_1} color="#00C805" />
                <LevelRow icon={Zap} label="TP 2" value={a.take_profit_2} color="#00C805" />
                <LevelRow icon={Zap} label="TP 3" value={a.take_profit_3} color="#00C805" />
            </div>

            {/* R:R */}
            <div className="flex items-center justify-between py-3 px-4 bg-white/[0.03] rounded-xl">
                <span className="text-xs tracking-[0.15em] uppercase font-bold text-zinc-500">Risk / Reward</span>
                <span className="text-lg font-mono font-bold text-[#007AFF]" data-testid="analysis-rr">{a.risk_reward_ratio}</span>
            </div>

            {/* Summary - Bigger text */}
            <div>
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-3">Analysis</p>
                <p className="text-sm text-zinc-200 leading-relaxed" data-testid="analysis-summary">{a.analysis_summary}</p>
            </div>

            {/* Indicators */}
            {a.indicators_used?.length > 0 && (
                <div>
                    <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-2">Patterns & Indicators</p>
                    <div className="flex flex-wrap gap-1.5">
                        {a.indicators_used.map((ind, i) => (
                            <Badge key={i} variant="outline" className="rounded-full text-xs text-zinc-300 border-white/10 px-3 py-1">{ind}</Badge>
                        ))}
                    </div>
                </div>
            )}

            {/* Key Levels */}
            {a.key_levels?.length > 0 && (
                <div>
                    <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-2">Key Levels</p>
                    <div className="space-y-1.5">
                        {a.key_levels.map((lv, i) => (
                            <div key={i} className="flex items-center justify-between text-sm">
                                <span className={`text-xs ${lv.type === 'support' ? 'text-[#00C805]' : 'text-[#FF3B30]'}`}>
                                    {lv.type} ({lv.strength})
                                </span>
                                <span className="font-mono text-white">${lv.price}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Timestamp */}
            <div className="flex items-center gap-1 text-xs text-zinc-600">
                <Clock className="w-3.5 h-3.5" />
                {new Date(data.created_at).toLocaleString()}
            </div>
        </div>
    );
}

function LevelRow({ icon: Icon, label, value, color }) {
    if (!value && value !== 0) return null;
    const formatted = typeof value === 'number' ? (value < 10 ? value.toFixed(4) : value >= 1000 ? value.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2}) : value.toFixed(2)) : value;
    return (
        <div className="flex items-center justify-between py-1">
            <div className="flex items-center gap-2.5">
                <Icon className="w-4 h-4" style={{ color }} />
                <span className="text-xs tracking-[0.15em] uppercase font-bold text-zinc-500">{label}</span>
            </div>
            <span className="text-base font-mono font-bold text-white">${formatted}</span>
        </div>
    );
}
